// define props of storyCard
import React, {useState} from "react";
import {Box, Button, CircularProgress, Fade, TextField, Typography} from "@material-ui/core/";
import {makeStyles} from "@material-ui/core/styles";
import {Skeleton} from "@material-ui/lab/";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckIcon from "@material-ui/icons/Check";
import {apiRequest} from "../util/util";
import {story} from "../utils/story";
import {StorySection} from "../utils/storySection";
import track from "../util/frontend_analytics";
import {useAuth} from "../util/auth";
import {forgetWall, rememberWall} from "../util/authReturn";
import {SignupPopup} from "./SignupPopup";


var editText: string;
const EDIT_LIMIT = 5000;  // the story engine refuses longer edits

const useStyles = makeStyles((theme) => ({
    section: {
        marginBottom: theme.spacing(2),
    },
    turn: {
        margin: theme.spacing(2.5, 0),
        padding: theme.spacing(1.25, 2),
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        borderRadius: theme.spacing(0, 1.5, 1.5, 0),
        backgroundColor: "rgba(236, 122, 150, 0.07)",
    },
    turnLabel: {
        display: "block",
        color: theme.palette.primary.main,
        lineHeight: 1.6,
        fontSize: "0.68rem",
    },
    turnText: {
        fontStyle: "italic",
        color: theme.palette.text.primary,
        fontSize: "1rem",
        lineHeight: 1.55,
        overflowWrap: "anywhere",
    },
    prose: {
        fontFamily: theme.typography.h1.fontFamily,
        fontSize: "1.0625rem",
        lineHeight: 1.6,
        color: "#EDE2E6",
        overflowWrap: "break-word",
        "& p": {
            margin: theme.spacing(0, 0, 1.5),
        },
        [theme.breakpoints.down("xs")]: {
            fontSize: "1rem",
            lineHeight: 1.6,
        },
    },
    ending: {
        textAlign: "center",
        padding: theme.spacing(4, 0, 2),
        "& p": {
            fontFamily: theme.typography.h1.fontFamily,
            fontSize: "1.6rem",
            fontStyle: "italic",
            margin: theme.spacing(0, 0, 1),
        },
        "&::before": {
            content: '"\\2726"',
            display: "block",
            color: theme.palette.primary.main,
            marginBottom: theme.spacing(2),
        },
    },
    figure: {
        margin: theme.spacing(2, 0),
        borderRadius: theme.spacing(2),
        overflow: "hidden",
        backgroundColor: "rgba(255, 220, 232, 0.05)",
        [theme.breakpoints.down("xs")]: {
            margin: theme.spacing(1.5, 0),
            borderRadius: theme.spacing(1.5),
        },
    },
    image: {
        display: "block",
        width: "100%",
        maxHeight: 340,
        objectFit: "cover",
        objectPosition: "center 25%",  // shorter frames shouldn't crop off faces
        [theme.breakpoints.down("xs")]: {
            maxHeight: 240,
        },
    },
    pictureFailed: {
        height: 340,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1.5),
        color: theme.palette.text.secondary,
        [theme.breakpoints.down("xs")]: {
            height: 240,
        },
    },
    // signed-out readers see the opener's picture, and every later one blurred behind a sign-in prompt
    locked: {
        position: "relative",
        height: 340,
        [theme.breakpoints.down("xs")]: {
            height: 240,
        },
    },
    lockedImage: {
        position: "absolute",
        inset: -24,
        width: "calc(100% + 48px)",
        height: "calc(100% + 48px)",
        objectFit: "cover",
        filter: "blur(22px) brightness(0.55)",
    },
    lockedPrompt: {
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        textAlign: "center",
        color: "#fff",
    },
    lockedTitle: {
        fontFamily: theme.typography.h1.fontFamily,
        fontSize: "1.25rem",
        fontWeight: 600,
    },
    imageSkeleton: {
        height: 340,
        borderRadius: 0,
        [theme.breakpoints.down("xs")]: {
            height: 240,
        },
    },
    actions: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(-1),
    },
    action: {
        color: theme.palette.text.secondary,
        fontWeight: 500,
        "&:hover": {
            color: theme.palette.text.primary,
        },
    },
    editor: {
        "& textarea": {
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.95rem",
            lineHeight: 1.6,
        },
    },
}));

interface StoryCardProps {
    id: string;
    text: string;
    userText: string | undefined;
    image: string | undefined;
    picture?: string;
    endOfStory: boolean;
    firstCard: boolean;
    lastCard: boolean
    storyId: string | undefined;
    readOnly: boolean;
    storySection?: StorySection;

}

function StoryCard(props: StoryCardProps) {
    // this is a card that contains the next lines of the story in our text adventure
    // animate the text getting written
    var startingText = '';
    var textcompleted = false;

    if (!props.lastCard || props.readOnly) {
        startingText = props.text;
        textcompleted = true;
    }
    const [textCompleted, setTextCompleted] = useState(true)

    const [text, setText] = React.useState(props.text);
    const [editable, setEditable] = React.useState(false);
    const [editProcessing, setEditProcessing] = React.useState(false);
    const [retrying, setRetrying] = React.useState(false);
    const [signupOpen, setSignupOpen] = React.useState(false);
    const auth = useAuth();
    const locked = auth && auth.user === false && !props.firstCard;  // false is signed out, null still loading

    // the owner can have a picture that didn't come through drawn again
    async function retryPicture() {
        setRetrying(true);
        try {
            const data = await apiRequest('ml', 'POST', {target: 'retry-picture', sessionId: story.id, sectionId: props.id});
            if (data && data.success) {
                props.storySection?.setPicture('pending', undefined);
                story.watchPictures();
            }
        } catch (e) {
            console.log(e);
        }
        setRetrying(false);
    }
    const [draft, setDraft] = React.useState<string | null>(null);  // a refused edit, kept in the reopened editor
    console.log('user text ', props.userText)
    const classes = useStyles();


    // @ts-ignore
    return (
        <div className={classes.section} data-story-part>
            {signupOpen && <SignupPopup signupModal={signupOpen} setSignupModal={(open: boolean) => {
                if (!open) forgetWall();  // closed without signing in: a later sign-in doesn't jump back here
                setSignupOpen(open);
            }}/>}
            {props.userText && props.userText.length > 0 &&
                <Box className={classes.turn}>
                    <Typography variant="overline" className={classes.turnLabel}>You</Typography>
                    <Typography variant="body1" className={classes.turnText}>{props.userText}</Typography>
                </Box>}
            <div key={props.id}>
                {props.endOfStory ? <DisplayCenteredText text={text} editable={editable}/> :
                    <DisplayText text={editable && draft !== null ? draft : text} editable={editable}/>}

                {textCompleted && !props.endOfStory && props.picture !== 'none' && !(props.picture === 'failed' && props.readOnly) && (
                    <Fade in timeout={1200}>
                        <figure className={classes.figure}>
                            {props.picture === 'failed' ? (
                                <Box className={classes.pictureFailed}>
                                    <Typography variant="body2">The picture didn't come through.</Typography>
                                    <Button variant="outlined" size="small" disabled={retrying} onClick={retryPicture}>
                                        Try again
                                    </Button>
                                </Box>
                            ) : locked ? (
                                <Box className={classes.locked}>
                                    {props.image && <img className={classes.lockedImage} src={props.image} alt="" aria-hidden="true"/>}
                                    <Box className={classes.lockedPrompt}>
                                        <Typography className={classes.lockedTitle}>A picture is drawn for every part of your story</Typography>
                                        <Typography variant="body2">You must be signed in to see images.</Typography>
                                        <Button variant="contained" color="primary" onClick={() => {
                                            track("story_picture_signup", {theme: story.theme});
                                            rememberWall(story.id, '');
                                            setSignupOpen(true);
                                        }}>
                                            Sign in free
                                        </Button>
                                    </Box>
                                </Box>
                            ) : props.image ? <img className={classes.image} src={props.image} alt="story image"/> :
                                <Skeleton variant={'rect'} animation="wave" className={classes.imageSkeleton}/>}
                        </figure>
                    </Fade>)}

                {!props.readOnly && props.lastCard && (
                    <Box className={classes.actions}>
                        {editable ?
                            <Button
                                disabled={editProcessing}
                                size="small"
                                startIcon={<CheckIcon fontSize="small"/>}
                                onClick={async () => {
                                    if (editText.length > EDIT_LIMIT) {
                                        alert(`A part can be at most 5,000 characters (this one has ${editText.length.toLocaleString()}).`)
                                        return
                                    }
                                    setEditable(false);
                                    story.setUnderEdit(false)
                                    setEditProcessing(true)
                                    const origText = props.text;
                                    // we want setEditProcessing to have an immediate effect and not wait for the api request to finish
                                    // so we use a callback function
                                    async function callback() {

                                        try {

                                            const data = await apiRequest('ml', 'POST', {
                                                target: 'edit',
                                                sessionId: props.storyId,
                                                sectionId: props.id,
                                                userText: editText,
                                                mode: 'edit'
                                            })
                                            if (data['success'] === false) {
                                                const message = data['message'] ?? 'Sorry! Your edit failed for some reason Please try something else'
                                                alert(message)
                                                setEditProcessing(false)
                                                setText(origText);
                                                setDraft(editText)
                                                setEditable(true)
                                                story.setUnderEdit(true)
                                                return
                                            }
                                            setText(editText);
                                            props.storySection?.updateText(editText)
                                            // an edited section with a drawn picture loses it and gets one drawn for the new text
                                            if (data.picture) {
                                                story.noteEdit()
                                                props.storySection?.setPicture(data.picture, data.imgUrl)
                                                story.watchPictures()
                                            }
                                            setEditProcessing(false)
                                            console.log(data)
                                        } catch (e) {
                                            alert('Sorry! Your edit failed for some reason Please try something else')
                                            setText(origText);
                                            props.storySection?.updateText(origText)
                                            setEditProcessing(false)
                                            setDraft(editText)
                                            setEditable(true)
                                            story.setUnderEdit(true)
                                        }
                                    }

                                    track('edit_submitted', {})
                                    callback()

                                }} variant={'contained'} color={"primary"}>Submit edit</Button>
                            :
                            <Button
                                disabled={editProcessing}
                                size="small"
                                className={classes.action}
                                startIcon={<EditOutlinedIcon fontSize="small"/>}
                                onClick={async () => {
                                    editText = text;
                                    setDraft(null)
                                    setEditable(true)
                                    story.setUnderEdit(true)
                                    track('edit_started', {})
                                }}>Edit</Button>
                        }
                        <Button
                            size="small"
                            className={classes.action}
                            startIcon={<DeleteOutlineIcon fontSize="small"/>}
                            onClick={async () => {
                                try {
                                    const data = await apiRequest('ml', 'POST', {
                                        target: 'edit',
                                        sessionId: props.storyId,
                                        sectionId: props.id,
                                        mode: 'delete'
                                    })
                                    story.deleteLastSection()
                                    track('section_deleted', {})
                                } catch (e) {
                                    console.error(e)
                                }

                            }}
                        >Delete</Button>
                        {editProcessing && (
                            <CircularProgress size={20}/>
                        )}
                    </Box>
                )}
            </div>
        </div>
    );


}

function EditableText(props: { text: string }) {
    const classes = useStyles();

    return (
        <Box sx={{width: "100%"}} mb={2}>

            <TextField
                fullWidth={true}
                id="outlined-multiline-static"
                variant="outlined"
                className={classes.editor}
                multiline
                defaultValue={props.text}
                onChange={(e) => {
                    editText = e.target.value
                }}
            />
        </Box>
    )
}


interface DisplayTextProps {
    text: string;
    editable: boolean;
}

function paragraphs(text: string) {
    return text.split('\n').filter((item) => item.trim().length > 0)
}

function DisplayText({text, editable}: DisplayTextProps) {
    const classes = useStyles();

    if (editable) {
        console.log('displaying editable')
        return <EditableText text={text}/>;
    }
    return (
        <div className={classes.prose} data-story-text>
            {paragraphs(text).map((item, key) => <p key={key}>{item}</p>)}
        </div>
    );
}


function DisplayCenteredText({text}: DisplayTextProps) {
    const classes = useStyles();

    return (
        <div className={classes.ending} data-story-text>
            {paragraphs(text).map((item, key) => <p key={key}>{item}</p>)}
        </div>
    );
}

export default StoryCard
